import { IndexedDBService } from './IndexedDBService';
import type { FinancialStatementType } from '../../types/reports';
import type { CurrencyCode } from '../../config/currency';

export interface GeneratedReport {
  id: string;
  type: FinancialStatementType;
  title: string;
  data: any;
  currency: CurrencyCode;
  generatedAt: string;
  generatedBy: string;
  organization: {
    name: string;
    registrationNumber: string;
  };
  isAudited: boolean;
  format: 'pdf' | 'excel' | 'json';
}

export class ReportsStorageService {
  private static STORE_NAME = 'generated_reports';
  private static DB_VERSION = 6; // Incrémenter la version pour ajouter le nouveau store

  /**
   * Initialise le service et s'assure que le store existe
   */
  static async init(): Promise<void> {
    // S'assurer que la base de données est initialisée avec le nouveau store
    await this.ensureStoreExists();
  }

  /**
   * S'assure que le store generated_reports existe dans IndexedDB
   */
  private static async ensureStoreExists(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('wanzo_accounting', this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        request.result.close();
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Créer le store s'il n'existe pas
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const reportsStore = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          reportsStore.createIndex('type', 'type');
          reportsStore.createIndex('generatedAt', 'generatedAt');
          reportsStore.createIndex('generatedBy', 'generatedBy');
          reportsStore.createIndex('currency', 'currency');
        }
      };
    });
  }

  /**
   * Sauvegarde un rapport généré dans IndexedDB
   */
  static async saveReport(report: Omit<GeneratedReport, 'id' | 'generatedAt'>): Promise<GeneratedReport> {
    try {
      await this.init();

      const newReport: GeneratedReport = {
        ...report,
        id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        generatedAt: new Date().toISOString()
      };

      await IndexedDBService.add(this.STORE_NAME, newReport);
      return newReport;
    } catch (error) {
      console.error('Error saving report to IndexedDB:', error);
      throw new Error('Failed to save report');
    }
  }

  /**
   * Récupère tous les rapports générés
   */
  static async getAllReports(): Promise<GeneratedReport[]> {
    try {
      await this.init();
      const reports = await IndexedDBService.getAll<GeneratedReport>(this.STORE_NAME);
      
      // Trier par date de génération (plus récent en premier)
      return reports.sort((a, b) => 
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
      );
    } catch (error) {
      console.error('Error getting reports from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Récupère un rapport par son ID
   */
  static async getReport(id: string): Promise<GeneratedReport | null> {
    try {
      await this.init();
      const report = await IndexedDBService.get<GeneratedReport>(this.STORE_NAME, id);
      return report || null;
    } catch (error) {
      console.error('Error getting report from IndexedDB:', error);
      return null;
    }
  }

  /**
   * Récupère les rapports par type
   */
  static async getReportsByType(type: FinancialStatementType): Promise<GeneratedReport[]> {
    try {
      await this.init();
      const reports = await IndexedDBService.getByIndex<GeneratedReport>(
        this.STORE_NAME,
        'type',
        type
      );
      
      // Trier par date de génération (plus récent en premier)
      return reports.sort((a, b) => 
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
      );
    } catch (error) {
      console.error('Error getting reports by type from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Supprime un rapport par son ID
   */
  static async deleteReport(id: string): Promise<void> {
    try {
      await this.init();
      await IndexedDBService.delete(this.STORE_NAME, id);
    } catch (error) {
      console.error('Error deleting report from IndexedDB:', error);
      throw new Error('Failed to delete report');
    }
  }

  /**
   * Supprime tous les rapports
   */
  static async clearAllReports(): Promise<void> {
    try {
      await this.init();
      await IndexedDBService.clear(this.STORE_NAME);
    } catch (error) {
      console.error('Error clearing reports from IndexedDB:', error);
      throw new Error('Failed to clear reports');
    }
  }

  /**
   * Compte le nombre total de rapports
   */
  static async countReports(): Promise<number> {
    try {
      await this.init();
      return await IndexedDBService.count(this.STORE_NAME);
    } catch (error) {
      console.error('Error counting reports from IndexedDB:', error);
      return 0;
    }
  }
}
